import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { api } from '~/lib/api';

type ScoringStatus = 'pending' | 'processing' | 'completed' | 'failed';

interface ScoringAdminStatusProps {
  orgId: string;
  deptId: string;
  jobId: string;
  applicationId: string;
  hasSession: boolean;
  score: {
    status: ScoringStatus;
    outcome: string | null;
  } | null;
}

function StatusBadge({ status }: { status: ScoringStatus }) {
  const { t } = useTranslation();
  const variants: Record<ScoringStatus, { label: string; className: string }> = {
    completed: {
      label: t('scoring.admin.status.completed'),
      className: 'text-green-600 border-green-200'
    },
    pending: {
      label: t('scoring.admin.status.pending'),
      className: 'text-yellow-600 border-yellow-200'
    },
    processing: {
      label: t('scoring.admin.status.processing'),
      className: 'text-blue-600 border-blue-200'
    },
    failed: {
      label: t('scoring.admin.status.failed'),
      className: 'text-destructive border-destructive/30'
    }
  };
  const v = variants[status];
  return (
    <Badge variant="outline" className={`gap-1 ${v.className}`}>
      {v.label}
    </Badge>
  );
}

function OutcomeBadge({ outcome }: { outcome: string }) {
  const { t } = useTranslation();
  const map: Record<string, string> = {
    pass: 'text-green-600 border-green-200',
    fail: 'text-destructive border-destructive/30',
    incomplete: 'text-yellow-600 border-yellow-200'
  };
  return (
    <Badge variant="outline" className={`gap-1 ${map[outcome] ?? ''}`}>
      {t(`scoring.outcomes.${outcome}`, { defaultValue: outcome })}
    </Badge>
  );
}

export function ScoringAdminStatus({
  orgId,
  deptId,
  jobId,
  applicationId,
  hasSession,
  score
}: ScoringAdminStatusProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const reprocess = useMutation({
    mutationFn: async () => {
      const res = await api.api.org[':orgId'].department[':deptId'].jobs[':jobId'].applications[
        ':applicationId'
      ]['scoring']['reprocess'].$post({
        param: { orgId, deptId, jobId, applicationId }
      });
      if (!res.ok) throw new Error('Reprocess failed');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['scoring', orgId, deptId, jobId, applicationId]
      });
      void queryClient.invalidateQueries({
        queryKey: ['applications', orgId, deptId, jobId]
      });
      void queryClient.invalidateQueries({
        queryKey: ['activity', orgId, deptId, jobId, applicationId]
      });
    }
  });

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-foreground">{t('scoring.admin.title')}</p>

      {score ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground">{t('scoring.admin.status.label')}</span>
            <StatusBadge status={score.status} />
          </div>
          {score.outcome && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-muted-foreground">{t('scoring.admin.outcome')}</span>
              <OutcomeBadge outcome={score.outcome} />
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('scoring.admin.noScore')}</p>
      )}

      {hasSession && (
        <Button
          size="sm"
          variant="outline"
          disabled={reprocess.isPending}
          onClick={() => {
            if (window.confirm(t('scoring.admin.reprocessConfirm'))) {
              reprocess.mutate();
            }
          }}
          className="gap-1"
        >
          <RefreshCw className="h-3 w-3" />
          {reprocess.isPending ? t('scoring.admin.reprocessing') : t('scoring.admin.reprocess')}
        </Button>
      )}
    </div>
  );
}
